import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import type { RetailItemFamily } from "src/dm/hooks/useRetailItemFamilies";

/**
 * Per-family analysis row driven by the parent.
 * `missingSiblingCount` is the count of family members not yet on the deal
 * (computed in the parent against `useDealItems` data).
 */
export interface FamilyPromptEntry {
  family: RetailItemFamily;
  /** RITEM_NOs in the family that are not yet on the deal. */
  missingSiblingCount: number;
}

interface Props {
  /** Retail-item number the user clicked "Add to deal" on. */
  retailItemNumber: number;
  /** One entry per family the item belongs to (incl. zero-missing for context). */
  entries: FamilyPromptEntry[];
  /** Add the whole family expansion for the given family code. */
  onAddFamily: (familyCode: number) => void;
  /** Skip family expansion and add only the clicked item. */
  onAddSingleItem: () => void;
  /** Cancel — don't add anything. */
  onCancel: () => void;
  /** When true, the buttons are disabled (a mutation is in flight). */
  busy?: boolean;
}

/**
 * Legacy "Add whole family?" modal port. Triggered when the user clicks
 * "Add to deal" on a search result whose family has siblings that are
 * not on the deal yet. Mirrors the `addWholeFamily` panel in
 * `Item/SearchItem.aspx` plus the `selectedFamily` label population on
 * `SearchItem.aspx.cs` line 238 / `AddRewardItem.aspx.cs` line 234.
 *
 * Multi-family items (legacy `additionalFamilyCodes`) — when an item
 * participates in more than one family, we list each family with its
 * own "Add this whole family" button. The user can also click "Add only
 * this item" to skip family expansion entirely.
 */
const AddItemFamilyPrompt = ({
  retailItemNumber,
  entries,
  onAddFamily,
  onAddSingleItem,
  onCancel,
  busy = false,
}: Props) => {
  const hasMissing = entries.some((e) => e.missingSiblingCount > 0);
  return (
    <Dialog title="This item belongs to a family" width={520} onClose={onCancel}>
      <p style={{ marginTop: 0, fontSize: "0.9rem" }}>
        Retail item <strong>#{retailItemNumber}</strong>{" "}
        {entries.length === 1 ? (
          <>belongs to family <strong>{entries[0].family.familyCode}</strong>.</>
        ) : (
          <>belongs to <strong>{entries.length}</strong> families.</>
        )}
        {hasMissing && (
          <>
            {" "}Some family members are not yet on this deal. Add the whole
            family, or add only the picked item?
          </>
        )}
      </p>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          padding: "0.5rem 0.75rem",
          marginBottom: "0.75rem",
          fontSize: "0.85rem",
          background: "#f8fafc",
        }}
      >
        {entries.map((entry) => {
          const missing = entry.missingSiblingCount;
          const total = entry.family.totalItemCount;
          return (
            <div
              key={entry.family.familyCode}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: "#0f172a" }}>
                  Family {entry.family.familyCode}
                  {entry.family.description && (
                    <span style={{ color: "#64748b", fontWeight: 400 }}>
                      {" — "}{entry.family.description}
                    </span>
                  )}
                </div>
                <div style={{ color: "#475569", fontSize: "0.78rem" }}>
                  {total} item{total === 1 ? "" : "s"} in family ·{" "}
                  {missing} missing from deal
                </div>
              </div>
              <Button
                size="small"
                themeColor="primary"
                fillMode={missing > 0 ? "solid" : "outline"}
                disabled={busy || missing === 0}
                onClick={() => onAddFamily(entry.family.familyCode)}
                title={
                  missing === 0
                    ? "Every item in this family is already on the deal"
                    : `Add ${missing} missing item(s) from this family`
                }
              >
                {missing === 0 ? "All on deal" : `Add all ${total}`}
              </Button>
            </div>
          );
        })}
      </div>
      <DialogActionsBar>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onAddSingleItem} disabled={busy}>
          {busy ? "Working…" : "Add only this item"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AddItemFamilyPrompt;
